// Distance avant l'entree dans le viewport a laquelle une video en autoplay commence a charger.
// Assez large pour que la lecture ait demarre quand le bloc devient visible, assez courte pour
// qu'une video situee bas dans la page ne soit jamais telechargee si le visiteur ne descend pas.
const AUTOPLAY_ROOT_MARGIN = '300px 0px';

// Nombre maximum de tentatives de lecture par video (voir #primeAndPlay).
const MAX_PLAY_ATTEMPTS = 4;

class DeferredMedia extends HTMLElement {
  isPlaying = false;
  mediaPoster = null;
  toggleMediaButton = null;
  #observer = null;
  #playAttempts = 0;
  #onVisibilityChange = null;

  connectedCallback() {
    this.mediaPoster = this.querySelector('[data-ref="deferred-media-poster"]');
    this.toggleMediaButton = this.querySelector('[data-ref="toggle-media-button"]');

    this.mediaPoster?.addEventListener('click', this.showDeferredMedia.bind(this));
    this.toggleMediaButton?.addEventListener('click', this.toggleMedia.bind(this));

    // Chargement paresseux des videos en autoplay.
    // Le <video autoplay> n'est plus ecrit en dur dans le HTML (il reste dans le <template>) :
    // il est monte a l'approche du viewport, ce qui evite de telecharger des dizaines de Mo
    // pour un bloc situe tres bas dans la page.
    if (this.hasAttribute('autoplay')) {
      this.#observeForAutoplay();
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    if (this.#onVisibilityChange) {
      document.removeEventListener('visibilitychange', this.#onVisibilityChange);
      this.#onVisibilityChange = null;
    }
  }

  #observeForAutoplay() {
    // Repli sans IntersectionObserver : on garde le comportement d'origine (chargement immediat).
    if (typeof IntersectionObserver === 'undefined') {
      this.loadContent(false);
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          if (!this.getAttribute('data-media-loaded')) {
            // focus: false — un focus() ici ferait sauter la page pendant le defilement.
            this.loadContent(false);
          } else {
            // Deja monte mais toujours en pause : cas du carrousel horizontal, ou la video
            // n'etait pas visible lateralement lors de la premiere tentative. On retente
            // maintenant qu'elle est reellement a l'ecran.
            const video = this.querySelector('video');
            if (video && video.paused) {
              this.#playAttempts = 0;
              this.#primeAndPlay(video);
            }
          }
          break;
        }
      },
      { rootMargin: AUTOPLAY_ROOT_MARGIN }
    );

    // L'observation est volontairement maintenue : elle sert aussi de filet pour relancer la
    // lecture quand une slide devient visible par defilement horizontal du carrousel.
    this.#observer.observe(this);
  }

  // Prepare une video montee dynamiquement puis lance la lecture de facon fiable.
  //
  // Trois causes d'echec de l'autoplay mobile sont traitees ici :
  // 1. Les attributs `muted` et `playsinline` ne sont honores de facon garantie que lors du
  //    parsing HTML initial. Sur un <video> clone depuis un <template> puis insere, iOS peut
  //    considerer la video comme non muette et refuser l'autoplay en ligne. On force donc les
  //    proprietes DOM en plus des attributs.
  // 2. Avec preload="none", play() est appele alors que readyState vaut 0 (aucune donnee) :
  //    la promesse est rejetee. On repasse preload a 'auto' au montage et on amorce le chargement.
  // 3. La promesse retournee par play() n'etait pas capturee : un rejet restait silencieux et la
  //    video ne repartait jamais. On retente desormais sur les evenements de disponibilite des
  //    donnees, et au retour de visibilite de l'onglet.
  #primeAndPlay(video) {
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', ''); // anciens iOS

    if (video.getAttribute('preload') === 'none') {
      video.preload = 'auto';
      video.setAttribute('preload', 'auto');
      if (video.readyState === 0) video.load();
    }

    const attempt = () => {
      if (!video.paused) return;
      if (this.#playAttempts >= MAX_PLAY_ATTEMPTS) return;
      this.#playAttempts += 1;

      const result = video.play();
      if (result && typeof result.catch === 'function') {
        result
          .then(() => {
            this.isPlaying = true;
          })
          .catch(() => {
            // Pas assez de donnees, ou lecture refusee pour l'instant : on retente des que le
            // navigateur signale que la video est jouable, plutot qu'immediatement.
            video.addEventListener('loadeddata', attempt, { once: true });
            video.addEventListener('canplay', attempt, { once: true });
          });
      } else {
        this.isPlaying = true;
      }
    };

    attempt();

    // Un onglet en arriere-plan met la lecture en pause : on relance au retour a l'ecran.
    if (!this.#onVisibilityChange) {
      this.#onVisibilityChange = () => {
        if (document.visibilityState !== 'visible') return;
        const v = this.querySelector('video');
        if (v && v.paused && v.hasAttribute('autoplay')) {
          this.#playAttempts = 0;
          attempt();
        }
      };
      document.addEventListener('visibilitychange', this.#onVisibilityChange);
    }
  }

  updatePlayPauseHint(isPlaying) {
    if (this.toggleMediaButton instanceof HTMLElement) {
      this.toggleMediaButton.classList.remove('hidden');

      const playIcon = this.toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);

      const pauseIcon = this.toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');

    this.querySelector('[data-ref="deferred-media-video"]')?.appendChild(content);

    // this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.querySelector('[ref="deferred-media-poster"]')?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      this.#playAttempts = 0;
      this.#primeAndPlay(content);
    }
  }

  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*',
      );
    } else {
      this.querySelector('video')?.play();
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*',
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}
